import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AgentLayout } from "@/components/AgentLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CircleNotch, MagnifyingGlass, PlayCircle, Plus, GraduationCap,
  PencilSimple, Trash, FilmReel,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const inputCls = "bg-black/40 border-white/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 text-white font-sans";

/**
 * Extract a YouTube video ID from any URL form:
 * - https://www.youtube.com/watch?v=ABC123
 * - https://youtu.be/ABC123
 * - https://www.youtube.com/embed/ABC123
 * - https://www.youtube.com/shorts/ABC123
 */
function youtubeId(url) {
  if (!url) return "";
  const trimmed = String(url).trim();
  const re = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const m = trimmed.match(re);
  return m ? m[1] : "";
}

function youtubeThumb(url) {
  const id = youtubeId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : "";
}

function youtubeEmbed(url) {
  const id = youtubeId(url);
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1` : url;
}

function TrainingCard({ training, onPlay, isAdmin, onEdit, onDelete }) {
  const cover = training.cover_image_url || youtubeThumb(training.video_url);
  const [imgOk, setImgOk] = useState(true);

  return (
    <article
      className="group relative rounded-xl border border-white/10 bg-ink-surface hover:border-[#D4AF37]/40 transition-colors overflow-hidden flex flex-col"
      data-testid={`training-card-${training.id}`}
    >
      {/* Cover */}
      <button
        type="button"
        onClick={() => onPlay(training)}
        className="relative aspect-video bg-black overflow-hidden block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/60"
        data-testid={`training-cover-${training.id}`}
      >
        {cover && imgOk ? (
          <img
            src={cover}
            alt={training.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-black to-zinc-900">
            <FilmReel size={56} weight="duotone" className="text-[#D4AF37]/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-2xl shadow-black/50 group-hover:scale-110 transition-transform">
            <PlayCircle size={36} weight="fill" className="text-black ml-1" />
          </div>
        </div>
      </button>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <GraduationCap size={12} weight="duotone" className="text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/90 font-sans">Training</span>
        </div>
        <h3 className="font-serif text-xl text-white tracking-tight mb-1 leading-snug">{training.title}</h3>
        {training.description && (
          <p className="text-sm text-white/60 font-sans mb-3 line-clamp-2">{training.description}</p>
        )}

        <div className="mt-auto pt-4 flex items-center gap-2">
          <Button
            onClick={() => onPlay(training)}
            className="flex-1 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] font-sans font-medium h-10"
            data-testid={`training-watch-${training.id}`}
          >
            <PlayCircle size={14} weight="fill" /> Watch
          </Button>
          {isAdmin && (
            <>
              <Button
                onClick={() => onEdit(training)}
                variant="outline"
                size="icon"
                className="h-10 w-10 border-white/15 bg-transparent hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 text-white/70"
                data-testid={`training-edit-${training.id}`}
                title="Edit training"
              >
                <PencilSimple size={14} weight="duotone" />
              </Button>
              <Button
                onClick={() => onDelete(training)}
                variant="outline"
                size="icon"
                className="h-10 w-10 border-white/15 bg-transparent hover:border-red-400/50 hover:bg-red-500/10 text-white/70 hover:text-red-300"
                data-testid={`training-delete-${training.id}`}
                title="Delete training"
              >
                <Trash size={14} weight="duotone" />
              </Button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function TrainingFormDialog({ open, training, onClose, onSaved }) {
  const isEdit = !!training;
  const [title, setTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(training?.title || "");
      setVideoUrl(training?.video_url || "");
      setCoverUrl(training?.cover_image_url || "");
      setDescription(training?.description || "");
    }
  }, [open, training]);

  // Auto-suggest YouTube thumb when the video URL changes and cover is empty
  useEffect(() => {
    if (videoUrl && !coverUrl) {
      const t = youtubeThumb(videoUrl);
      if (t) setCoverUrl(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !videoUrl.trim()) {
      toast.error("Title and video URL are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        video_url: videoUrl.trim(),
        cover_image_url: coverUrl.trim(),
        description: description.trim(),
      };
      if (isEdit) {
        await api.put(`/admin/trainings/${training.id}`, payload);
        toast.success("Training updated");
      } else {
        await api.post("/admin/trainings", payload);
        toast.success("Training added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save training");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-ink-surface border-white/10 text-white max-w-xl" data-testid="training-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">
            {isEdit ? "Edit" : "Add"} <span className="italic text-[#D4AF37]">training</span>
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs font-sans">
            Drop in a YouTube link and we'll auto-fetch the thumbnail. Override the cover image if you want a custom one.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. How to build your first itinerary"
              className={inputCls}
              data-testid="training-title-input"
              required
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">YouTube link</Label>
            <Input
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className={inputCls}
              data-testid="training-video-url-input"
              required
            />
            {videoUrl && !youtubeId(videoUrl) && (
              <p className="text-[11px] text-amber-300/80 mt-1.5 font-sans">
                ⚠ Doesn't look like a YouTube link — embed may not work. Use the URL from the video's "Share" button.
              </p>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">Cover image (optional)</Label>
            <Input
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://... (auto-filled from YouTube)"
              className={inputCls}
              data-testid="training-cover-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">Description (optional)</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What will agents learn from this video?"
              className={inputCls}
              data-testid="training-description-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} className="border-white/15 bg-transparent text-white/80 hover:bg-white/5">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest" data-testid="training-form-save">
              {busy ? <CircleNotch size={14} className="animate-spin" /> : (isEdit ? "Save changes" : "Add training")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TrainingPlayerDialog({ open, training, onClose }) {
  const embed = training ? youtubeEmbed(training.video_url) : "";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-black border-white/10 text-white max-w-4xl p-0 overflow-hidden" data-testid="training-player-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5">
          <DialogTitle className="font-serif text-2xl pr-8 leading-snug">{training?.title}</DialogTitle>
          {training?.description && (
            <DialogDescription className="text-white/50 text-xs font-sans pr-4 line-clamp-2">
              {training.description}
            </DialogDescription>
          )}
        </DialogHeader>
        {open && training && (
          <div className="aspect-video bg-black w-full">
            {youtubeId(training.video_url) ? (
              <iframe
                src={embed}
                title={training.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="training-iframe"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/60 gap-3 px-6 text-center">
                <FilmReel size={48} weight="duotone" className="text-[#D4AF37]/60" />
                <p className="font-sans text-sm">This isn't a YouTube link — opening in a new tab.</p>
                <a
                  href={training.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:text-[#F3C853] underline text-sm font-sans"
                >
                  {training.video_url}
                </a>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Training() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trainings");
      setRows(data.rows || []);
    } catch (e) {
      toast.error("Failed to load trainings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((t) =>
      (t.title || "").toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const remove = async (training) => {
    if (!confirm(`Delete "${training.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/trainings/${training.id}`);
      toast.success("Training deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <AgentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <header className="mb-8 sm:mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="training-header">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-sans mb-2">
              Best Time · Academy
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              <span className="italic text-[#D4AF37]">Training</span> library
            </h1>
            <p className="text-white/60 font-sans text-sm mt-3 max-w-xl">
              Short videos to help your agents master every workflow — from itinerary building to closing the booking.
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-11 px-5 self-start sm:self-end"
              data-testid="training-add-btn"
            >
              <Plus size={16} weight="bold" /> Add training
            </Button>
          )}
        </header>

        {/* Search */}
        {rows.length > 0 && (
          <div className="mb-8 max-w-md relative">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trainings…"
              className={`${inputCls} pl-10 h-11`}
              data-testid="training-search-input"
            />
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <CircleNotch size={28} className="animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-xl px-8 py-16 text-center" data-testid="training-empty">
            <FilmReel size={56} weight="duotone" className="text-[#D4AF37]/50 mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-white mb-2">No trainings yet</h3>
            <p className="text-white/50 font-sans text-sm max-w-md mx-auto mb-6">
              {isAdmin
                ? "Add your first training video — paste a YouTube link and we'll auto-fetch the cover."
                : "Your travel director hasn't published any training videos yet. Check back soon."}
            </p>
            {isAdmin && (
              <Button
                onClick={() => { setEditing(null); setFormOpen(true); }}
                className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest"
                data-testid="training-empty-add-btn"
              >
                <Plus size={16} weight="bold" /> Add your first training
              </Button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-xl px-8 py-12 text-center text-white/50 font-sans text-sm">
            No trainings match "{search}".
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="training-grid">
            {filtered.map((t) => (
              <TrainingCard
                key={t.id}
                training={t}
                onPlay={(tr) => setPlaying(tr)}
                isAdmin={isAdmin}
                onEdit={(tr) => { setEditing(tr); setFormOpen(true); }}
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </div>

      <TrainingFormDialog
        open={formOpen}
        training={editing}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />

      <TrainingPlayerDialog
        open={!!playing}
        training={playing}
        onClose={() => setPlaying(null)}
      />
    </AgentLayout>
  );
}
